import React, { useState, useEffect } from 'react';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import { styles } from '../../styles/styles.js';
import { getConfig, updateConfig } from '../../services/tradingApi';

const ALL_LEAGUES = ['NFL', 'NBA', 'MLB', 'NHL', 'NCAAF', 'NCAAB', 'EPL', 'MLS', 'AFL', 'NRL'];
const ALL_BET_TYPES = ['moneyline', 'spread', 'total'];
const STRATEGY_OPTIONS = ['kelly_half', 'kelly_full', 'kelly_quarter', 'fixed_pct', 'flat'];

const inputStyle = { backgroundColor: '#222', color: 'white', border: '1px solid #444' };

const TradingSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await getConfig();
        setConfig(data);
      } catch (err) {
        console.error('Error fetching config:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const { data } = await updateConfig(config);
      setConfig(data);
      setMessage('Settings saved');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setMessage('Error saving settings');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const toggleListItem = (field, item) => {
    const list = config[field] || [];
    const next = list.includes(item)
      ? list.filter(i => i !== item)
      : [...list, item];
    handleChange(field, next);
  };

  if (loading) return <div style={{ color: 'white', padding: '2rem' }}>Loading...</div>;
  if (!config) return <div style={{ color: '#888', padding: '2rem' }}>Failed to load config</div>;

  return (
    <div style={{ padding: '1rem', maxWidth: '800px' }}>
      <h2 style={{ color: 'white', marginBottom: '20px' }}>Trading Settings</h2>

      {/* Master switches */}
      <Card style={{ ...styles.cardbutton_solo, marginBottom: '15px' }}>
        <Card.Body>
          <div style={{ color: '#888', fontSize: '12px', marginBottom: '10px' }}>Master Controls</div>
          <Form.Check
            type="switch"
            id="enabled"
            label={<span style={{ color: 'white' }}>Trading Enabled</span>}
            checked={config.enabled}
            onChange={(e) => handleChange('enabled', e.target.checked)}
            style={{ marginBottom: '10px' }}
          />
          <Form.Check
            type="switch"
            id="auto_approve"
            label={<span style={{ color: 'white' }}>Auto-Approve Orders</span>}
            checked={config.auto_approve}
            onChange={(e) => handleChange('auto_approve', e.target.checked)}
          />
        </Card.Body>
      </Card>

      {/* Strategy */}
      <Card style={{ ...styles.cardbutton_solo, marginBottom: '15px' }}>
        <Card.Body>
          <div style={{ color: '#888', fontSize: '12px', marginBottom: '10px' }}>Strategy</div>
          <Form.Group className="mb-3">
            <Form.Label style={{ color: 'white' }}>Active Strategy</Form.Label>
            <Form.Select
              value={config.active_strategy}
              onChange={(e) => handleChange('active_strategy', e.target.value)}
              style={inputStyle}
            >
              {STRATEGY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label style={{ color: 'white' }}>Strategy Config (JSON)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={JSON.stringify(config.strategy_config, null, 2)}
              onChange={(e) => {
                try { handleChange('strategy_config', JSON.parse(e.target.value)); }
                catch {} // ignore parse errors while typing
              }}
              style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '12px' }}
            />
          </Form.Group>
        </Card.Body>
      </Card>

      {/* Risk limits */}
      <Card style={{ ...styles.cardbutton_solo, marginBottom: '15px' }}>
        <Card.Body>
          <div style={{ color: '#888', fontSize: '12px', marginBottom: '10px' }}>Risk Limits</div>
          {[
            { field: 'max_bet_size', label: 'Max Bet Size ($)', step: '1' },
            { field: 'max_daily_exposure', label: 'Max Daily Exposure ($)', step: '1' },
            { field: 'max_single_game_exposure', label: 'Max Single Game Exposure ($)', step: '1' },
            { field: 'min_edge_threshold', label: 'Min Edge Threshold', step: '0.01' },
            { field: 'min_liquidity_contracts', label: 'Min Liquidity Contracts', step: '1' },
          ].map(({ field, label, step }) => (
            <Form.Group className="mb-2" key={field}>
              <Form.Label style={{ color: 'white', fontSize: '13px' }}>{label}</Form.Label>
              <Form.Control
                type="number"
                step={step}
                value={config[field]}
                onChange={(e) => handleChange(field, e.target.value)}
                style={{ ...inputStyle, width: '200px' }}
              />
            </Form.Group>
          ))}
        </Card.Body>
      </Card>

      {/* League & bet type filters */}
      <Card style={{ ...styles.cardbutton_solo, marginBottom: '15px' }}>
        <Card.Body>
          <div style={{ color: '#888', fontSize: '12px', marginBottom: '10px' }}>Filters (empty = all enabled)</div>
          <div style={{ color: 'white', fontSize: '13px', marginBottom: '5px' }}>Leagues</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '15px' }}>
            {ALL_LEAGUES.map(l => (
              <Form.Check
                key={l}
                type="checkbox"
                id={`league-${l}`}
                label={<span style={{ color: 'white' }}>{l}</span>}
                checked={(config.enabled_leagues || []).includes(l)}
                onChange={() => toggleListItem('enabled_leagues', l)}
              />
            ))}
          </div>
          <div style={{ color: 'white', fontSize: '13px', marginBottom: '5px' }}>Bet Types</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {ALL_BET_TYPES.map(t => (
              <Form.Check
                key={t}
                type="checkbox"
                id={`bet-${t}`}
                label={<span style={{ color: 'white' }}>{t}</span>}
                checked={(config.enabled_bet_types || []).includes(t)}
                onChange={() => toggleListItem('enabled_bet_types', t)}
              />
            ))}
          </div>
        </Card.Body>
      </Card>

      {/* Advanced */}
      <Card style={{ ...styles.cardbutton_solo, marginBottom: '15px' }}>
        <Card.Body>
          <div
            style={{ color: '#888', fontSize: '12px', cursor: 'pointer' }}
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            Advanced {showAdvanced ? '▼' : '▶'}
          </div>
          {showAdvanced && (
            <div style={{ marginTop: '10px' }}>
              {[
                { field: 'order_check_interval_seconds', label: 'Order Check Interval (sec)' },
                { field: 'settlement_check_interval_seconds', label: 'Settlement Check Interval (sec)' },
                { field: 'stale_order_ttl_minutes', label: 'Stale Order TTL (min)' },
                { field: 'max_preparations_per_cycle', label: 'Max Preparations Per Cycle' },
              ].map(({ field, label }) => (
                <Form.Group className="mb-2" key={field}>
                  <Form.Label style={{ color: 'white', fontSize: '13px' }}>{label}</Form.Label>
                  <Form.Control
                    type="number"
                    value={config[field]}
                    onChange={(e) => handleChange(field, e.target.value)}
                    style={{ ...inputStyle, width: '200px' }}
                  />
                </Form.Group>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
        {message && <span style={{ color: message.includes('Error') ? '#f44336' : '#4caf50' }}>{message}</span>}
      </div>
    </div>
  );
};

export default TradingSettings;
